import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BankAccountAdjustment {
  id: string;
  bank_account_id: string;
  previous_balance: number;
  new_balance: number;
  adjustment_amount: number;
  adjustment_date: string;
  reason: string;
  notes: string | null;
  created_by: string | null;
  created_at: string | null;
}

export interface CreateAdjustmentData {
  bank_account_id: string;
  previous_balance: number;
  new_balance: number;
  adjustment_date: string;
  reason: string;
  notes?: string;
  created_by?: string;
}

export function useBankAccountAdjustments(bankAccountId?: string) {
  return useQuery({
    queryKey: ['bank-account-adjustments', bankAccountId],
    queryFn: async () => {
      let query = supabase
        .from('bank_account_adjustments')
        .select(`
          *,
          created_by_user:app_users!bank_account_adjustments_created_by_fkey(display_name, first_name, last_name)
        `)
        .order('adjustment_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (bankAccountId) {
        query = query.eq('bank_account_id', bankAccountId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!bankAccountId || bankAccountId === undefined,
  });
}

export function useCreateBankAccountAdjustment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAdjustmentData) => {
      const { data: result, error } = await supabase
        .from('bank_account_adjustments')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bank-account-adjustments'] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}
